import React, { useState, useContext } from 'react';
import hive from '@bombas/hive';
import PropTypes from 'prop-types';
import { useBreakpoint } from '../../util/Breakpoint.context';
import Tooltip from '../Tooltip/Tooltip';
import * as Styled from './ColorSelection.styled';
import { ContainerQueryContext } from '../../util/ContainerQuery.context';

/**
 * Renders swatches with a tooltip
 *
 * If there are more swatches than the swatch limit, it renders a link that reads '+X more'
 */
const Option = ({ children, withTooltip, isOpen }) => {
  const [anchorElement, setAnchorElement] = useState(null);
  return (
    <Styled.Swatch
      isOpen={isOpen}
      onMouseEnter={(e) => setAnchorElement(e.currentTarget)}
      onMouseLeave={() => setAnchorElement(null)}
    >
      {children}
      {withTooltip && children.props.label && (
        <Tooltip
          anchorElement={anchorElement}
          onClose={setAnchorElement}
          action="hover"
          offset={32}
          placement="top"
          textTransform="capitalize"
        >
          {children.props.label}
        </Tooltip>
      )}
    </Styled.Swatch>
  );
};

/**
 * Displays up to 6 swatches on a product card.
 *
 * If more than 6 are available, a link to the PDP is shown.
 */
const ColorSelection = ({ children, isOpen, link, alwaysOpen }) => {
  const { styleQueryMode } = useContext(ContainerQueryContext);
  const { large } = useBreakpoint();
  const visibleSwatches = 3;
  const swatchLimit = 6;
  const swatches = React.Children.map(children, (child, i) => (
    <Option key={i} withTooltip={large} isOpen={isOpen}>
      {React.cloneElement(child, {
        responsiveSize: {
          small: Styled.MINI_SWATCH_HEIGHT,
          medium: Styled.REGULAR_SWATCH_HEIGHT,
          large: Styled.REGULAR_SWATCH_HEIGHT
        }
      })}
    </Option>
  ));
  const showMoreLink = swatches.length > swatchLimit && !alwaysOpen;

  return (
    <Styled.ColorSelection isOpen={isOpen} $styleQueryMode={styleQueryMode}>
      <Styled.Dropdown
        isOpen={isOpen}
        $showMoreLink={showMoreLink}
        $styleQueryMode={styleQueryMode}
      >
        {swatches}
        {link && showMoreLink && (
          <Styled.Link
            onClick={() => {
              hive.track('PRODUCT_CARD_SEE_MORE_CLICK');
            }}
            href={link}
            isOpen={isOpen}
            $styleQueryMode={styleQueryMode}
          >
            +{swatches.length - visibleSwatches}
          </Styled.Link>
        )}
      </Styled.Dropdown>
    </Styled.ColorSelection>
  );
};

Option.propTypes = {
  /**
   * An individual swatch passed from ColorSelection
   */
  children: PropTypes.node.isRequired,
  /**
   * Whether to display Tooltip
   */
  withTooltip: PropTypes.bool,
  /**
   * A trigger to reveal more swatches
   */
  isOpen: PropTypes.bool
};

Option.defaultProps = {
  withTooltip: false,
  isOpen: false
};

ColorSelection.propTypes = {
  /**
   * A flat array of swatches
   */
  children: PropTypes.arrayOf(PropTypes.node).isRequired,
  /**
   * A trigger to reveal more swatches
   */
  isOpen: PropTypes.bool,
  /**
   * A link to the product's PDP
   */
  link: PropTypes.string,
  /**
   * @deprecated - Never used, to be removed
   * Forces the ColorSelection component reveal all color swatches by default
   */
  alwaysOpen: PropTypes.bool
};

ColorSelection.defaultProps = {
  isOpen: false,
  link: undefined,
  alwaysOpen: false
};

export default ColorSelection;
